import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Button, Form, Alert, Card, Spinner } from "react-bootstrap";
import { formatIndianCurrency } from "../utils/razorpay";

const MembershipRenewalTerms = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [renewalInfo, setRenewalInfo] = useState(null);

  const memberData = location.state?.memberData;
  const membershipType = location.state?.membershipType;
  const baseUrl = process.env.REACT_APP_BASEURL;

  const isLifeMember = membershipType === 'full' || membershipType === 'life';

  const lifeTermsText = `
1. Membership renewals must be completed online through the AIOTA website.
2. You are required to upload a copy of your AIOTA Life Membership certificate and a recent passport-size photograph during the renewal process.
3. If your membership certificate has been lost, you must submit a declaration letter addressed to the Honorary Secretary, along with your life membership number.
4. Renewal Fees:
   • Before Expiration: ₹5,900 (including 18% GST).  
   • After Expiration: ₹5,900 (including 18% GST) + ₹500 late fee.
5. Renewal of an expired membership does not reset your validity. The 15-year term will continue to be calculated from your original renewal due date.
6. Renewal payments are strictly non-refundable once processed.
7. Payments can only be made online through net banking, credit card, debit card, or UPI. Offline methods such as cheques or demand drafts will not be accepted.
8. After successful payment:
   • A soft copy of your renewed Life Membership certificate and ID card will be issued within 10–12 business days.  
   • No hard copies will be issued.
9. Your membership number will be updated upon renewal.
10. In case of duplicate or accidental double payments, you must contact the Treasurer via email to request a refund.
`;

  const associateTermsText = `
1. Membership renewals must be completed online through the AIOTA website.
2. You are required to upload a copy of your AIOTA Associate Membership certificate and a recent passport-size photograph during the renewal process.
3. Renewal Fees:
   • ₹1,770 (including 18% GST).
4. Valid for one financial year only.
5. Allows participation in conferences but not elections.
6. Renewal payments are strictly non-refundable once processed.
7. Payments can only be made online through net banking, credit card, debit card, or UPI. Offline methods such as cheques or demand drafts will not be accepted.
8. After successful payment:
   • A soft copy of your renewed Associate Membership certificate and ID card will be issued within 10–12 business days.  
   • No hard copies will be issued.
9. Your membership number will be updated upon renewal.
10. In case of duplicate or accidental double payments, you must contact the Treasurer via email to request a refund.
`;

  const termsText = isLifeMember ? lifeTermsText : associateTermsText;


  useEffect(() => {
    if (location.state?.renewalInfo) {
      setRenewalInfo(location.state.renewalInfo);
    }
  }, [location.state]);

  // Utility function to get current date dynamically
  const getCurrentDate = () => {
    return new Date();
  };

  // Utility function to check if membership is expired
  const isMembershipExpired = (validTillDate) => {
    if (!validTillDate) return false;
    const currentDate = getCurrentDate();
    const expiryDate = new Date(validTillDate);
    return expiryDate < currentDate;
  };

  // Calculate renewal fees (fallback if no renewal info from backend)
  const getRenewalFees = () => {
    const isLifeMember = membershipType === 'full' || membershipType === 'life';

    if (renewalInfo && isLifeMember) {
      const renewalTotal = renewalInfo.amount_IN_renewal || 5900;
      const baseAmount = Math.round(renewalTotal / 1.18);
      const gst = renewalTotal - baseAmount;

      // Check if membership is expired using utility function
      const isExpired = isMembershipExpired(renewalInfo.valid_till);
      
      console.log('Date comparison:', {
        currentDate: getCurrentDate().toISOString(),
        validTillDate: renewalInfo.valid_till,
        isExpired
      });

      let lateFee = 0;
      let lateGst = 0;
      let totalAmount = renewalTotal;

      // Include late fee if membership is expired AND renewal_late_fee is provided
      if (isExpired && renewalInfo.renewal_late_fee && renewalInfo.renewal_late_fee > 0) {
        const lateTotal = renewalInfo.renewal_late_fee;
        lateFee = Math.round(lateTotal / 1.18);
        lateGst = lateTotal - lateFee;
        totalAmount += lateTotal;
        
        console.log('Late fee calculation (expired membership):', {
          lateTotal,
          lateFee,
          lateGst,
          totalAmount,
          isExpired,
          currentDate: getCurrentDate().toISOString(),
          validTillDate: renewalInfo.valid_till
        });
      }

    return {
        baseAmount,
        gst,
        lateFee,
        lateGst,
        totalAmount
      };
    }
    
    // Fallback: Try to get late fee from memberData if renewalInfo doesn't have it
    if (memberData && isLifeMember) {
      const renewalTotal = memberData.amount_IN_renewal || 5900;
      const baseAmount = Math.round(renewalTotal / 1.18);
      const gst = renewalTotal - baseAmount;

      // Check if membership is expired using utility function
      const isExpired = isMembershipExpired(memberData.validTill);

      let lateFee = 0;
      let lateGst = 0;
      let totalAmount = renewalTotal;

      // Include late fee if membership is expired AND provided in member data
      if (isExpired && memberData.renewal_late_fee && memberData.renewal_late_fee > 0) {
        const lateTotal = memberData.renewal_late_fee;
        lateFee = Math.round(lateTotal / 1.18);
        lateGst = lateTotal - lateFee;
        totalAmount += lateTotal;
        
        console.log('Late fee calculation from memberData (expired membership):', {
          lateTotal,
          lateFee,
          lateGst,
          totalAmount,
          isExpired,
          currentDate: getCurrentDate().toISOString(),
          validTillDate: memberData.validTill || 'N/A'
        });
      }

      return {
        baseAmount,
        gst,
        lateFee,
        lateGst,
        totalAmount
      };
    }

    // Fallback calculation
    const baseAmount = isLifeMember ? 5500 : 1500; // Base amount without GST
    const gst = baseAmount * 0.18; // 18% GST
    const lateFee = isLifeMember ? 500 : 0; // Late fee if applicable
    const lateGst = lateFee * 0.18; // GST on late fee

    return {
      baseAmount: Math.round(baseAmount),
      gst: Math.round(gst),
      lateFee: Math.round(lateFee),
      lateGst: Math.round(lateGst),
      totalAmount: Math.round(baseAmount + gst + lateFee + lateGst)
    };
  };

  const fees = getRenewalFees();
  
  // Debug logging
  console.log('MembershipRenewalTerms Debug:', {
    renewalInfo,
    memberData,
    membershipType,
    fees
  });

  const handleProceed = () => {
    if (!accepted) {
      setError("You must accept the terms and conditions to proceed.");
      return;
    }

    if (!memberData || !membershipType) {
      setError("Invalid membership data. Please try again.");
      return;
    }

    // Navigate to payment with all required data
    navigate("/membership-renewal-payment", {
      state: {
        memberData,
        membershipType,
        paymentDetails: {
          amount: fees.totalAmount,
          breakdown: {
            baseAmount: fees.baseAmount,
            gst: fees.gst,
            lateFee: fees.lateFee,
            lateGst: fees.lateGst
          }
        },
        renewalInfo: renewalInfo,
        acceptedTerms: true
      }
    });
  };

  if (!memberData || !membershipType) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          Invalid access. Please start the renewal process from your membership dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Membership Renewal - Terms & Conditions</h2>
      
      <Card className="mb-4">
        <Card.Header as="h5">Renewal Details</Card.Header>
        <Card.Body>
          <div className="mb-3">
            <strong>Member Name:</strong> {memberData.name}<br />
            <strong>Membership Type:</strong> {membershipType === 'full' ? 'Life Member' : 'Associate Member'}<br />
          </div>
          
          <div className="fee-breakdown">
            <h6>Fee Breakdown:</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>Base Amount:</td>
                  <td>{formatIndianCurrency(fees.baseAmount)}</td>
                </tr>
                <tr>
                  <td>GST (18%):</td>
                  <td>{formatIndianCurrency(fees.gst)}</td>
                </tr>
                {fees.lateFee > 0 && (
                  <>
                    <tr>
                      <td>Late Fee:</td>
                      <td>{formatIndianCurrency(fees.lateFee)}</td>
                    </tr>
                    <tr>
                      <td>Late Fee GST (18%):</td>
                      <td>{formatIndianCurrency(fees.lateGst)}</td>
                    </tr>
                  </>
                )}
                <tr className="table-primary">
                  <td><strong>Total Amount:</strong></td>
                  <td><strong>{formatIndianCurrency(fees.totalAmount)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      <Alert variant="info" className="mb-4">
        Please read and accept the terms and conditions before proceeding to payment.
      </Alert>

      <div className="terms-container mb-4 p-3 bg-light border rounded">
        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{termsText}</pre>
      </div>

      <Form.Check
        type="checkbox"
        id="terms-checkbox"
        label="I have read and accept the terms and conditions"
        checked={accepted}
        onChange={(e) => {
          setAccepted(e.target.checked);
          setError("");
        }}
        className="mb-3"
      />

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="d-flex gap-2">
        <Button
          variant="primary"
          className="w-auto"
          onClick={handleProceed}
          disabled={!accepted}
        >
          Proceed to Payment
        </Button>
       <Button
  variant="outline-secondary"
  onClick={() => navigate("/membership-renewal-upload", {
    state: {
      ...location.state,
      certificateFile: location.state?.certificateFile,
      photoFile: location.state?.photoFile,
    }
  })}
>
  Go Back
</Button>
      </div>
    </Container>
  );
};

export default MembershipRenewalTerms;